import type { SuretyAccountId } from "@inrev/common";
import { useAdminFetchAccountCompanies } from "@portal/api/account/admin";
import {
	useAdminCreateBondRequest,
	useFetchAdminBondRequestOutcome,
} from "@portal/api/bond-request/admin";
import { type ReactNode, useMemo } from "react";
import {
	type BondRequestSubmissionAdapter,
	BondRequestSubmissionAdapterProvider,
} from "./BondRequestSubmissionAdapterContext";

type AdminBondRequestSubmissionAdapterProviderProps = {
	accountId: SuretyAccountId;
	children: ReactNode;
};

export const AdminBondRequestSubmissionAdapterProvider = ({
	accountId,
	children,
}: AdminBondRequestSubmissionAdapterProviderProps) => {
	const { createBondRequest } = useAdminCreateBondRequest();
	const { companies, companiesIsLoading } = useAdminFetchAccountCompanies(accountId);

	const adapter = useMemo<BondRequestSubmissionAdapter>(
		() => ({
			submitBondRequest: (payload, { accountId, agentId, onSuccess, onError }) => {
				if (!agentId || !accountId) {
					onError();
					return;
				}
				createBondRequest({
					accountId,
					data: {
						agentId,
						quote: payload.quote,
					},
				}).then(
					(data) => onSuccess(data.id),
					() => onError(),
				);
			},
			useOutcomeQuery: (quoteId) => useFetchAdminBondRequestOutcome(accountId, quoteId),
			companies,
			companiesIsLoading,
		}),
		[createBondRequest, accountId, companies, companiesIsLoading],
	);

	return (
		<BondRequestSubmissionAdapterProvider value={adapter}>
			{children}
		</BondRequestSubmissionAdapterProvider>
	);
};
